import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  UserCircle,
  Briefcase,
  Globe,
  DollarSign,
  Plane,
  Loader2,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Copy,
  Check,
  Lock,
  ShieldCheck,
  ChevronLeft,
  ChevronRight,
  MapPin,
  GraduationCap,
  Scale,
  MessageSquare,
  Calendar,
  Car,
  Heart,
  Users,
  FileText,
  Shield,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

/* ------------------------------------------------------------------ */
/*  Reusable "select with Other free-text" component                  */
/* ------------------------------------------------------------------ */
function SelectWithOther({ label, name, value, onChange, options, placeholder, icon: Icon, required, hint }) {
  const isOther = value && !options.some((o) => (typeof o === 'string' ? o : o.value) === value);
  const [showCustom, setShowCustom] = useState(isOther);

  const handleSelect = (e) => {
    const v = e.target.value;
    if (v === '__other__') {
      setShowCustom(true);
      onChange({ target: { name, value: '' } });
    } else {
      setShowCustom(false);
      onChange({ target: { name, value: v } });
    }
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-1.5 flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
        {label}
        {required && <span className="text-rose-500 text-xs">*</span>}
      </label>
      {!showCustom ? (
        <select
          name={name}
          value={value || ''}
          onChange={handleSelect}
          className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 bg-white transition-all"
        >
          <option value="">{placeholder || 'Select an option...'}</option>
          {options.map((o) => {
            const val = typeof o === 'string' ? o : o.value;
            const lbl = typeof o === 'string' ? o : o.label;
            return (
              <option key={val} value={val}>
                {lbl}
              </option>
            );
          })}
          <option value="__other__">Other (type your own)</option>
        </select>
      ) : (
        <div className="flex gap-2">
          <input
            type="text"
            name={name}
            value={value || ''}
            onChange={onChange}
            placeholder="Type your answer..."
            className="flex-1 px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all"
          />
          <button
            type="button"
            onClick={() => { setShowCustom(false); onChange({ target: { name, value: '' } }); }}
            className="px-3 py-2 text-xs text-slate-500 hover:text-slate-700 border border-slate-200 rounded-xl hover:bg-slate-50 transition-all"
          >
            List
          </button>
        </div>
      )}
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Circular Progress Indicator                                        */
/* ------------------------------------------------------------------ */
function CircularProgress({ percentage }) {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;
  const color =
    percentage < 30 ? '#ef4444' : percentage < 60 ? '#f59e0b' : percentage < 90 ? '#6366f1' : '#10b981';

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width="96" height="96" viewBox="0 0 96 96" className="-rotate-90">
        <circle cx="48" cy="48" r={radius} fill="none" stroke="#e2e8f0" strokeWidth="7" />
        <circle
          cx="48"
          cy="48"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-700 ease-out"
        />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className="text-xl font-extrabold text-slate-800">{percentage}%</span>
        <span className="text-[9px] text-slate-400 font-semibold uppercase tracking-wider">Complete</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Step definitions                                                   */
/* ------------------------------------------------------------------ */
const STEPS = [
  { key: 'personal', label: 'Personal Details', icon: UserCircle },
  { key: 'job', label: 'Job Preferences', icon: Briefcase },
  { key: 'education', label: 'Education & Skills', icon: GraduationCap },
  { key: 'location', label: 'Location & Travel', icon: MapPin },
  { key: 'background', label: 'Background & Legal', icon: Scale },
  { key: 'eeo', label: 'EEO Demographics', icon: Users },
  { key: 'communication', label: 'Communication', icon: MessageSquare },
];

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */
export default function CareerProfile() {
  const { profile, updateProfile } = useAuth();

  /* --- Assigned Account card state --- */
  const [showPassword, setShowPassword] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    if (type === 'email') {
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    } else {
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    }
  };

  /* --- Multi-step wizard state --- */
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  /* --- Form data (all fields) --- */
  const [formData, setFormData] = useState({
    // existing
    phone: profile?.phone || '',
    target_role: profile?.target_role || '',
    target_location: profile?.target_location || '',
    target_salary: profile?.target_salary || '',
    visa_status: profile?.visa_status || 'not_required',
    portfolio_url: profile?.portfolio_url || '',
    social_media_url: profile?.social_media_url || '',
    ssn: profile?.ssn || '',
    // personal
    date_of_birth: profile?.date_of_birth || '',
    address: profile?.address || '',
    available_from: profile?.available_from || '',
    customer_type: profile?.customer_type || 'New',
    // job preferences
    years_of_experience: profile?.years_of_experience || '',
    employment_type: profile?.employment_type || '',
    w2_or_1099: profile?.w2_or_1099 || '',
    preferred_jobs: profile?.preferred_jobs || '',
    hourly_wage: profile?.hourly_wage || '',
    annual_salary: profile?.annual_salary || '',
    accept_lower_offer: profile?.accept_lower_offer || 'No',
    // education
    education_level: profile?.education_level || '',
    other_education: profile?.other_education || '',
    education_start: profile?.education_start || '',
    education_end: profile?.education_end || '',
    languages: profile?.languages || '',
    certifications: profile?.certifications || '',
    // location & travel
    authorized_in_usa: profile?.authorized_in_usa || 'No',
    willing_to_travel: profile?.willing_to_travel || 'No (0%)',
    international_travel: profile?.international_travel || 'No',
    work_anywhere_usa: profile?.work_anywhere_usa || 'No',
    preferred_state: profile?.preferred_state || '',
    remote_anywhere_usa: profile?.remote_anywhere_usa || 'No',
    // background
    references_list: profile?.references_list || '',
    drivers_license: profile?.drivers_license || 'No',
    work_on_weekends: profile?.work_on_weekends || 'No',
    convicted_of_crime: profile?.convicted_of_crime || 'No',
    crime_explanation: profile?.crime_explanation || '',
    // eeo
    gender: profile?.gender || '',
    hispanic_latino: profile?.hispanic_latino || 'No',
    race: profile?.race || '',
    veteran_status: profile?.veteran_status || 'No',
    disability: profile?.disability || 'No',
    // communication
    referral_source: profile?.referral_source || '',
    do_not_apply_to: profile?.do_not_apply_to || '',
    message_to_specialist: profile?.message_to_specialist || '',
  });

  /* Track whether user has manually edited the form */
  const formDirty = useRef(false);
  const handleChange = (e) => {
    formDirty.current = true;
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  /* Re-sync formData when profile updates (e.g. OAuth backfill) while form is pristine */
  useEffect(() => {
    if (!profile || formDirty.current) return;
    setFormData(prev => {
      const next = { ...prev };
      for (const key of Object.keys(prev)) {
        if (profile[key] != null && profile[key] !== '') {
          next[key] = profile[key];
        }
      }
      return next;
    });
  }, [profile]);

  /* --- Check if a specific step's required fields are completed --- */
  const isStepCompleted = (stepKey) => {
    switch (stepKey) {
      case 'personal':
        return !!(
          formData.date_of_birth && formData.date_of_birth.trim() !== '' &&
          formData.available_from && formData.available_from.trim() !== '' &&
          formData.phone && formData.phone.trim() !== '' &&
          formData.address && formData.address.trim() !== '' &&
          formData.customer_type && formData.customer_type.trim() !== ''
        );
      case 'job':
        return !!(
          formData.preferred_jobs && formData.preferred_jobs.trim() !== '' &&
          formData.target_role && formData.target_role.trim() !== '' &&
          formData.years_of_experience && formData.years_of_experience.trim() !== '' &&
          formData.employment_type && formData.employment_type.trim() !== '' &&
          formData.w2_or_1099 && formData.w2_or_1099.trim() !== '' &&
          formData.hourly_wage && formData.hourly_wage.trim() !== '' &&
          formData.annual_salary && formData.annual_salary.trim() !== '' &&
          formData.target_salary && formData.target_salary.trim() !== '' &&
          formData.accept_lower_offer && formData.accept_lower_offer.trim() !== ''
        );
      case 'education':
        return !!(
          formData.education_level && formData.education_level.trim() !== '' &&
          formData.education_start && formData.education_start.trim() !== '' &&
          formData.education_end && formData.education_end.trim() !== '' &&
          formData.languages && formData.languages.trim() !== ''
        );
      case 'location':
        return !!(
          formData.target_location && formData.target_location.trim() !== '' &&
          formData.authorized_in_usa && formData.authorized_in_usa.trim() !== '' &&
          formData.visa_status && formData.visa_status.trim() !== '' &&
          formData.willing_to_travel && formData.willing_to_travel.trim() !== '' &&
          formData.international_travel && formData.international_travel.trim() !== '' &&
          formData.work_anywhere_usa && formData.work_anywhere_usa.trim() !== '' &&
          formData.preferred_state && formData.preferred_state.trim() !== '' &&
          formData.remote_anywhere_usa && formData.remote_anywhere_usa.trim() !== ''
        );
      case 'background':
        return !!(
          formData.references_list && formData.references_list.trim() !== '' &&
          formData.drivers_license && formData.drivers_license.trim() !== '' &&
          formData.work_on_weekends && formData.work_on_weekends.trim() !== '' &&
          formData.convicted_of_crime && formData.convicted_of_crime.trim() !== '' &&
          (formData.convicted_of_crime !== 'Yes' || (formData.crime_explanation && formData.crime_explanation.trim() !== ''))
        );
      case 'eeo':
        return !!(
          formData.gender && formData.gender.trim() !== '' &&
          formData.hispanic_latino && formData.hispanic_latino.trim() !== '' &&
          formData.race && formData.race.trim() !== '' &&
          formData.veteran_status && formData.veteran_status.trim() !== '' &&
          formData.disability && formData.disability.trim() !== ''
        );
      case 'communication':
        return !!(
          formData.referral_source && formData.referral_source.trim() !== '' &&
          formData.do_not_apply_to && formData.do_not_apply_to.trim() !== ''
        );
      default:
        return false;
    }
  };

  /* --- Compute completion percentage --- */
  const percentage = useMemo(() => {
    const requiredFields = [
      'date_of_birth', 'available_from', 'phone', 'address', 'customer_type',
      'preferred_jobs', 'target_role', 'years_of_experience', 'employment_type', 'w2_or_1099', 'hourly_wage', 'annual_salary', 'target_salary', 'accept_lower_offer',
      'education_level', 'education_start', 'education_end', 'languages',
      'target_location', 'authorized_in_usa', 'visa_status', 'willing_to_travel', 'international_travel', 'work_anywhere_usa', 'preferred_state', 'remote_anywhere_usa',
      'references_list', 'drivers_license', 'work_on_weekends', 'convicted_of_crime',
      'gender', 'hispanic_latino', 'race', 'veteran_status', 'disability',
      'referral_source', 'do_not_apply_to'
    ];
    let filledCount = 0;
    requiredFields.forEach((k) => {
      if (formData[k] && formData[k].trim() !== '') {
        filledCount++;
      }
    });
    // Add criminal explanation check if Yes
    if (formData.convicted_of_crime === 'Yes') {
      if (formData.crime_explanation && formData.crime_explanation.trim() !== '') {
        filledCount++;
      }
      return Math.round((filledCount / (requiredFields.length + 1)) * 100);
    }
    return Math.round((filledCount / requiredFields.length) * 100);
  }, [formData]);

  /* --- Submit handler (saves ALL fields at once) --- */
  const handleSubmit = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    // Handle postgres date empty string bug (must send null, not "")
    const payload = { ...formData };
    const dateFields = ['date_of_birth', 'available_from', 'education_start', 'education_end'];
    dateFields.forEach((f) => {
      if (payload[f] === '') {
        payload[f] = null;
      }
    });

    try {
      const { error } = await updateProfile(payload);
      if (error) throw error;
      formDirty.current = false;
      setMessage({ type: 'success', text: 'Career profile saved successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to update profile.' });
    } finally {
      setSaving(false);
    }
  };

  /* shared input/textarea classes */
  const inputCls =
    'w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all bg-white';
  const labelCls = 'block text-sm font-semibold text-slate-700 mb-1.5 flex items-center gap-2';

  /* ---------------------------------------------------------------- */
  /*  Step content renderers                                           */
  /* ---------------------------------------------------------------- */
  const renderStep = () => {
    switch (STEPS[currentStep].key) {
      /* ── Step 1: Personal Details ── */
      case 'personal':
        return (
          <div className="space-y-5 animate-fadeIn">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>
                  <Calendar className="w-4 h-4 text-slate-400" /> Date of Birth <span className="text-rose-500 text-xs">*</span>
                </label>
                <input type="date" name="date_of_birth" value={formData.date_of_birth} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>
                  <Calendar className="w-4 h-4 text-slate-400" /> Available From <span className="text-rose-500 text-xs">*</span>
                </label>
                <input type="date" name="available_from" value={formData.available_from} onChange={handleChange} className={inputCls} />
              </div>
            </div>

            <div>
              <label className={labelCls}>📱 Phone Number <span className="text-rose-500 text-xs">*</span></label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+1 (555) 123-4567" className={inputCls} />
              <p className="mt-1 text-xs text-slate-400">We use this number to contact you about bookings and updates.</p>
            </div>

            <div>
              <label className={labelCls}>
                <MapPin className="w-4 h-4 text-slate-400" /> Address <span className="text-rose-500 text-xs">*</span>
              </label>
              <textarea name="address" value={formData.address} onChange={handleChange} rows="2" placeholder="123 Main St, City, State, ZIP" className={inputCls + ' resize-none'} />
            </div>

            <SelectWithOther
              label="Customer Type"
              name="customer_type"
              value={formData.customer_type}
              onChange={handleChange}
              options={['New', 'Existing']}
              icon={UserCircle}
              required
            />

            {/* Web Presence (existing) */}
            <div className="pt-4 border-t border-slate-100">
              <h4 className="text-sm font-bold text-slate-600 mb-4">Web Presence & Identity</h4>
              <div className="space-y-4">
                <div>
                  <label className={labelCls}>
                    <Globe className="w-4 h-4 text-slate-400" /> LinkedIn & Social Media URLs (Optional)
                  </label>
                  <textarea name="social_media_url" value={formData.social_media_url} onChange={handleChange} placeholder="https://linkedin.com/in/username" rows="2" className={inputCls + ' resize-none'} />
                </div>
                <div>
                  <label className={labelCls}>
                    <FileText className="w-4 h-4 text-slate-400" /> Portfolio / Personal Websites (Optional)
                  </label>
                  <textarea name="portfolio_url" value={formData.portfolio_url} onChange={handleChange} placeholder="https://yourportfolio.com" rows="2" className={inputCls + ' resize-none'} />
                </div>
                <div>
                  <label className={labelCls}>
                    <Lock className="w-4 h-4 text-slate-400" /> Social Security Number (Optional)
                  </label>
                  <input type="text" name="ssn" value={formData.ssn} onChange={handleChange} placeholder="Required by some US employers" className={inputCls} />
                </div>
              </div>
            </div>
          </div>
        );

      /* ── Step 2: Job Preferences ── */
      case 'job':
        return (
          <div className="space-y-5 animate-fadeIn">
            <div>
              <label className={labelCls}>
                <Briefcase className="w-4 h-4 text-slate-400" /> Preferred Job Titles <span className="text-rose-500 text-xs">*</span>
              </label>
              <textarea name="preferred_jobs" value={formData.preferred_jobs} onChange={handleChange} rows="3" placeholder="Operations Manager, Program Manager, Business Analyst..." className={inputCls + ' resize-none'} />
              <p className="mt-1 text-xs text-slate-400">Separate multiple titles with commas.</p>
            </div>

            <div>
              <label className={labelCls}>
                <Briefcase className="w-4 h-4 text-slate-400" /> Target Job Titles <span className="text-rose-500 text-xs">*</span>
              </label>
              <input type="text" name="target_role" value={formData.target_role} onChange={handleChange} placeholder="e.g. Senior Product Manager" className={inputCls} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <SelectWithOther
                label="Years of Experience"
                name="years_of_experience"
                value={formData.years_of_experience}
                onChange={handleChange}
                options={['0-1 years', '1-3 years', '3-5 years', '5-7 years', '7-10 years', '10-15 years', '15+ years']}
                icon={Briefcase}
                required
              />
              <SelectWithOther
                label="Employment Type"
                name="employment_type"
                value={formData.employment_type}
                onChange={handleChange}
                options={['Full Time', 'Part Time', 'Contract', 'Temporary', 'Internship', 'Freelance']}
                icon={Briefcase}
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <SelectWithOther
                label="W2 or 1099"
                name="w2_or_1099"
                value={formData.w2_or_1099}
                onChange={handleChange}
                options={['W2', '1099', 'Both', 'N/A']}
                icon={FileText}
                required
              />
              <div>
                <label className={labelCls}>
                  <DollarSign className="w-4 h-4 text-slate-400" /> Hourly Wage <span className="text-rose-500 text-xs">*</span>
                </label>
                <input type="text" name="hourly_wage" value={formData.hourly_wage} onChange={handleChange} placeholder="e.g. $35/hr" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>
                  <DollarSign className="w-4 h-4 text-slate-400" /> Annual Salary <span className="text-rose-500 text-xs">*</span>
                </label>
                <input type="text" name="annual_salary" value={formData.annual_salary} onChange={handleChange} placeholder="e.g. $80,000" className={inputCls} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>
                  <DollarSign className="w-4 h-4 text-slate-400" /> Target Salary Range <span className="text-rose-500 text-xs">*</span>
                </label>
                <input type="text" name="target_salary" value={formData.target_salary} onChange={handleChange} placeholder="e.g. $80k - $120k" className={inputCls} />
              </div>
              <SelectWithOther
                label="Accept Lower Offer?"
                name="accept_lower_offer"
                value={formData.accept_lower_offer}
                onChange={handleChange}
                options={['Yes', 'No']}
                icon={DollarSign}
                required
              />
            </div>
          </div>
        );

      /* ── Step 3: Education & Skills ── */
      case 'education':
        return (
          <div className="space-y-5 animate-fadeIn">
            <SelectWithOther
              label="Education Level"
              name="education_level"
              value={formData.education_level}
              onChange={handleChange}
              options={[
                'High School / GED',
                "Associate's Degree",
                "Bachelor's Degree",
                "Master's Degree",
                'MBA',
                'PhD / Doctorate',
                'Professional Certification',
                'Trade / Vocational',
              ]}
              icon={GraduationCap}
              required
            />

            <div>
              <label className={labelCls}>
                <GraduationCap className="w-4 h-4 text-slate-400" /> Other Education
              </label>
              <input type="text" name="other_education" value={formData.other_education} onChange={handleChange} placeholder="Certifications, bootcamps, etc." className={inputCls} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className={labelCls}>
                  <Calendar className="w-4 h-4 text-slate-400" /> Education Start <span className="text-rose-500 text-xs">*</span>
                </label>
                <input type="date" name="education_start" value={formData.education_start} onChange={handleChange} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>
                  <Calendar className="w-4 h-4 text-slate-400" /> Education End <span className="text-rose-500 text-xs">*</span>
                </label>
                <input type="date" name="education_end" value={formData.education_end} onChange={handleChange} className={inputCls} />
              </div>
            </div>

            <div>
              <label className={labelCls}>
                <Globe className="w-4 h-4 text-slate-400" /> Languages <span className="text-rose-500 text-xs">*</span>
              </label>
              <input type="text" name="languages" value={formData.languages} onChange={handleChange} placeholder="English, Spanish, French..." className={inputCls} />
              <p className="mt-1 text-xs text-slate-400">Separate with commas.</p>
            </div>

            <div>
              <label className={labelCls}>
                <FileText className="w-4 h-4 text-slate-400" /> Certifications (Optional)
              </label>
              <textarea name="certifications" value={formData.certifications} onChange={handleChange} rows="2" placeholder="PMP, AWS Certified, Six Sigma..." className={inputCls + ' resize-none'} />
            </div>
          </div>
        );

      /* ── Step 4: Location & Travel ── */
      case 'location':
        return (
          <div className="space-y-5 animate-fadeIn">
            <div>
              <label className={labelCls}>
                <Globe className="w-4 h-4 text-slate-400" /> Preferred Locations / Markets <span className="text-rose-500 text-xs">*</span>
              </label>
              <input type="text" name="target_location" value={formData.target_location} onChange={handleChange} placeholder="e.g. Remote (US/Canada), United Kingdom" className={inputCls} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <SelectWithOther
                label="Authorized to Work in USA?"
                name="authorized_in_usa"
                value={formData.authorized_in_usa}
                onChange={handleChange}
                options={['Yes', 'No']}
                icon={Shield}
                required
              />
              <SelectWithOther
                label="Needs Visa Sponsorship?"
                name="visa_status"
                value={formData.visa_status}
                onChange={handleChange}
                options={[
                  { value: 'not_required', label: 'I do not require sponsorship' },
                  { value: 'required', label: 'I require visa sponsorship' },
                  { value: 'only_remote', label: 'Only targeting fully remote roles' },
                ]}
                icon={Plane}
                required
              />
            </div>

            <SelectWithOther
              label="Willing to Travel"
              name="willing_to_travel"
              value={formData.willing_to_travel}
              onChange={handleChange}
              options={['No (0%)', 'Yes (25%)', 'Yes (50%)', 'Yes (75%)', 'Yes (100%)']}
              icon={Plane}
              required
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <SelectWithOther
                label="International Travel?"
                name="international_travel"
                value={formData.international_travel}
                onChange={handleChange}
                options={['Yes', 'No']}
                icon={Plane}
                required
              />
              <SelectWithOther
                label="Work Anywhere in USA?"
                name="work_anywhere_usa"
                value={formData.work_anywhere_usa}
                onChange={handleChange}
                options={['Yes', 'No']}
                icon={MapPin}
                required
              />
            </div>

            <SelectWithOther
              label="Preferred State"
              name="preferred_state"
              value={formData.preferred_state}
              onChange={handleChange}
              options={[
                'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
                'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
                'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
                'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
                'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
                'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
                'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
                'Wisconsin','Wyoming','Remote Only','Any / No Preference',
              ]}
              icon={MapPin}
              required
            />

            <SelectWithOther
              label="Remote Anywhere in USA?"
              name="remote_anywhere_usa"
              value={formData.remote_anywhere_usa}
              onChange={handleChange}
              options={['Yes', 'No']}
              icon={Globe}
              required
            />
          </div>
        );

      /* ── Step 5: Background & Legal ── */
      case 'background':
        return (
          <div className="space-y-5 animate-fadeIn">
            <div>
              <label className={labelCls}>
                <FileText className="w-4 h-4 text-slate-400" /> References <span className="text-rose-500 text-xs">*</span>
              </label>
              <textarea name="references_list" value={formData.references_list} onChange={handleChange} rows="3" placeholder="Name — Title — Company — Phone — Email (one per line)" className={inputCls + ' resize-none'} />
              <p className="mt-1 text-xs text-slate-400">Provide at least 2-3 professional references.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <SelectWithOther
                label="Driver's License?"
                name="drivers_license"
                value={formData.drivers_license}
                onChange={handleChange}
                options={['Yes', 'No']}
                icon={Car}
                required
              />
              <SelectWithOther
                label="Work on Weekends?"
                name="work_on_weekends"
                value={formData.work_on_weekends}
                onChange={handleChange}
                options={['Yes', 'No']}
                icon={Calendar}
                required
              />
            </div>

            <SelectWithOther
              label="Convicted of a Crime?"
              name="convicted_of_crime"
              value={formData.convicted_of_crime}
              onChange={handleChange}
              options={['Yes', 'No']}
              icon={Scale}
              required
            />

            {formData.convicted_of_crime === 'Yes' && (
              <div className="animate-fadeIn">
                <label className={labelCls}>
                  <FileText className="w-4 h-4 text-slate-400" /> Explanation <span className="text-rose-500 text-xs">*</span>
                </label>
                <textarea name="crime_explanation" value={formData.crime_explanation} onChange={handleChange} rows="3" placeholder="Please provide details..." className={inputCls + ' resize-none'} />
              </div>
            )}
          </div>
        );

      /* ── Step 6: EEO Demographics ── */
      case 'eeo':
        return (
          <div className="space-y-5 animate-fadeIn">
            <div className="bg-amber-50 border border-amber-200/60 rounded-xl p-4 text-xs text-amber-800 flex items-start gap-2.5">
              <Shield className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <strong className="font-bold">Voluntary Self-Identification</strong>
                <p className="mt-1 leading-relaxed">
                  This information is collected solely to comply with employer EEO reporting requirements.
                  It is <strong>optional</strong> (select "Prefer Not to Say" if you wish), kept confidential, and will <em>never</em> be used in
                  hiring or placement decisions.
                </p>
              </div>
            </div>

            <SelectWithOther
              label="Gender"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              options={['Male', 'Female', 'Non-Binary', 'Prefer Not to Say']}
              icon={Users}
              required
            />

            <SelectWithOther
              label="Hispanic / Latino?"
              name="hispanic_latino"
              value={formData.hispanic_latino}
              onChange={handleChange}
              options={['Yes', 'No', 'Prefer Not to Say']}
              icon={Users}
              required
            />

            <SelectWithOther
              label="Race"
              name="race"
              value={formData.race}
              onChange={handleChange}
              options={[
                'American Indian or Alaska Native',
                'Asian',
                'Black or African American',
                'Native Hawaiian or Other Pacific Islander',
                'White',
                'Two or More Races',
                'Prefer Not to Say',
              ]}
              icon={Users}
              required
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <SelectWithOther
                label="Veteran Status"
                name="veteran_status"
                value={formData.veteran_status}
                onChange={handleChange}
                options={['Yes', 'No', 'Prefer Not to Say']}
                icon={Shield}
                required
              />
              <SelectWithOther
                label="Disability"
                name="disability"
                value={formData.disability}
                onChange={handleChange}
                options={['Yes', 'No', 'Prefer Not to Say']}
                icon={Heart}
                required
              />
            </div>
          </div>
        );

      /* ── Step 7: Communication ── */
      case 'communication':
        return (
          <div className="space-y-5 animate-fadeIn">
            <SelectWithOther
              label="Referral Source"
              name="referral_source"
              value={formData.referral_source}
              onChange={handleChange}
              options={[
                'Former Colleague',
                'Friend / Family',
                'LinkedIn',
                'Google Search',
                'Social Media',
                'Job Board',
                'Recruiter',
                'Company Website',
                'Event / Conference',
              ]}
              icon={Users}
              required
            />

            <div>
              <label className={labelCls}>
                <AlertCircle className="w-4 h-4 text-slate-400" /> Do Not Apply To <span className="text-rose-500 text-xs">*</span>
              </label>
              <textarea
                name="do_not_apply_to"
                value={formData.do_not_apply_to}
                onChange={handleChange}
                rows="4"
                placeholder="Please do not apply to commission only positions, insurance sales roles, MLM opportunities, call center positions, or heavily sales driven roles..."
                className={inputCls + ' resize-none'}
              />
              <p className="mt-1 text-xs text-slate-400">List any roles, companies, or industries you want us to avoid.</p>
            </div>

            <div>
              <label className={labelCls}>
                <MessageSquare className="w-4 h-4 text-slate-400" /> Message to Specialist
              </label>
              <textarea
                name="message_to_specialist"
                value={formData.message_to_specialist}
                onChange={handleChange}
                rows="5"
                placeholder="Share anything else we should know — your career goals, constraints, preferences, or special instructions..."
                className={inputCls + ' resize-none'}
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Navigation helpers                                               */
  /* ---------------------------------------------------------------- */
  const goNext = () => {
    if (currentStep < STEPS.length - 1) setCurrentStep(currentStep + 1);
  };
  const goPrev = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  /* ---------------------------------------------------------------- */
  /*  JSX                                                              */
  /* ---------------------------------------------------------------- */
  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Career Profile</h1>
        <p className="mt-1 text-slate-500">
          Complete all sections to unlock job matching. The more you fill, the better we can find your perfect role.
        </p>
      </div>

      {/* ── Assigned Account Card (preserved) ── */}
      {profile?.assigned_email && (
        <div className="bg-gradient-to-br from-indigo-900 to-indigo-950 text-white rounded-3xl p-6 sm:p-8 shadow-xl shadow-indigo-950/20 relative overflow-hidden border border-indigo-950">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-2xl pointer-events-none"></div>

          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2.5">
              <div className="p-2.5 bg-indigo-500/20 rounded-xl text-indigo-250 border border-indigo-500/30">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white leading-tight">Dedicated Application Account</h3>
                <p className="text-xs text-indigo-300">We use this account to search and apply for jobs on your behalf.</p>
              </div>
            </div>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500/10 border border-emerald-500/25 rounded-full text-xs font-bold text-emerald-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
              Secure Portal Encrypted
            </span>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            {/* Email */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center justify-between gap-4 transition-all hover:bg-white/8">
              <div className="space-y-1 min-w-0">
                <span className="text-[10px] text-indigo-300 uppercase font-extrabold tracking-wider block">Email Address</span>
                <span className="font-semibold text-slate-100 text-sm sm:text-base truncate block">{profile.assigned_email}</span>
              </div>
              <button
                onClick={() => copyToClipboard(profile.assigned_email, 'email')}
                className="p-2 bg-white/10 hover:bg-white/20 text-slate-350 hover:text-white rounded-xl transition-all flex-shrink-0"
                title="Copy Email"
              >
                {copiedEmail ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>

            {/* Password */}
            {profile?.assigned_email_password && (
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center justify-between gap-4 transition-all hover:bg-white/8">
                <div className="space-y-1 min-w-0">
                  <span className="text-[10px] text-indigo-300 uppercase font-extrabold tracking-wider block">Account Password</span>
                  <span className="font-semibold text-slate-100 text-sm sm:text-base tracking-wide truncate block">
                    {showPassword ? profile.assigned_email_password : '••••••••••••'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-2 bg-white/10 hover:bg-white/20 text-slate-350 hover:text-white rounded-xl transition-all"
                    title={showPassword ? 'Hide Password' : 'Show Password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => copyToClipboard(profile.assigned_email_password, 'password')}
                    className="p-2 bg-white/10 hover:bg-white/20 text-slate-350 hover:text-white rounded-xl transition-all"
                    title="Copy Password"
                  >
                    {copiedPassword ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 flex items-start gap-2 bg-white/5 border border-white/5 rounded-xl p-3.5 text-xs text-indigo-200">
            <Lock className="w-4 h-4 text-indigo-300 flex-shrink-0 mt-0.5" />
            <p>
              <strong>Security Protocol:</strong> These credentials are encrypted end-to-end and stored securely. To ensure maximum safety, avoid writing them down or sharing them. Only copy them in private environments.
            </p>
          </div>
        </div>
      )}

      {/* ── Main Wizard Card ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {/* ── Header with circular progress ── */}
        <div className="p-6 sm:p-8 bg-gradient-to-br from-slate-50 to-white border-b border-slate-100">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <CircularProgress percentage={percentage} />
            <div className="text-center sm:text-left">
              <h3 className="text-xl font-bold text-slate-900">Job Application Profile</h3>
              <p className="text-sm text-slate-500 mt-1">
                Step {currentStep + 1} of {STEPS.length} — <span className="font-semibold text-indigo-600">{STEPS[currentStep].label}</span>
              </p>
            </div>
          </div>

          {/* Premium Notice Box */}
          <div className="mt-5 bg-amber-50/50 border border-amber-200/50 rounded-2xl p-4 flex items-start gap-3 text-amber-850 text-xs animate-fadeIn">
            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <span className="font-bold text-amber-900 block mb-0.5">Required for Job Placement</span>
              <p className="leading-relaxed text-amber-700">
                To enable our career specialists to successfully match and apply for jobs on your behalf, please ensure all profile details are fully completed.
              </p>
            </div>
          </div>

          {/* ── Linear progress bar ── */}
          <div className="mt-6">
            <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500 ease-out"
                style={{
                  width: `${((currentStep + 1) / STEPS.length) * 100}%`,
                  background: 'linear-gradient(90deg, #6366f1, #818cf8)',
                }}
              />
            </div>
          </div>

          {/* ── Step pills ── */}
          <div className="mt-4 flex flex-wrap gap-2">
            {STEPS.map((step, i) => {
              const StepIcon = step.icon;
              const isActive = i === currentStep;
              const isCompleted = isStepCompleted(step.key);
              return (
                <button
                  type="button"
                  key={step.key}
                  onClick={() => setCurrentStep(i)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                    isActive
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200'
                      : isCompleted
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-slate-50 text-slate-500 border border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  {isCompleted ? (
                    <CheckCircle className="w-3.5 h-3.5" />
                  ) : (
                    <StepIcon className="w-3.5 h-3.5" />
                  )}
                  <span className="hidden sm:inline">{step.label}</span>
                  <span className="sm:hidden">{i + 1}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Step Content ── */}
        <div className="p-6 sm:p-8">
          {/* Notification */}
          {message.text && (
            <div
              className={`mb-6 p-4 rounded-xl flex items-center gap-3 text-sm ${
                message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'
              }`}
            >
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
              )}
              <span>{message.text}</span>
            </div>
          )}

          {renderStep()}
        </div>

        {/* ── Navigation Footer ── */}
        <div className="px-6 sm:px-8 py-5 border-t border-slate-100 bg-slate-50/50 flex items-center justify-between">
          <button
            type="button"
            onClick={goPrev}
            disabled={currentStep === 0}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>

          <div className="flex items-center gap-3">
            {/* Save at any step */}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="px-5 py-2.5 text-sm font-semibold text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-xl hover:bg-indigo-100 transition-all disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save
            </button>

            {currentStep < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={goNext}
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-bold text-white bg-emerald-600 rounded-xl hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Save & Complete
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
